import { useCallback, useEffect, useMemo } from 'react';
import * as React from 'react';
import { UseFormReturn, useWatch } from 'react-hook-form';
import { FieldPath } from 'react-hook-form/dist/types/path';

import { EnumListKey, useAllEnums } from '~/data/enums';
import { useHasChanged } from '~/hooks/useHasChanged';
import {
  CurrentEmploymentSalary,
  SalaryProposalFormData,
} from '~/scenes/RewardingModule/components/EmployeeProposalsDialog/components/EmployeeProposal/components/Forms/SalaryProposalForm/data';
import {
  getRegex_IsNumberToDecimals,
  mathFloorToDecimals,
  mathRoundToDecimals,
  regex_IsNumber,
} from '~/utils/helper';

type SalaryCalculation = {
  baseSalary: number;
  raiseAmount: number;
  raisePercentage: number;
  newSalary: number;
};

enum SalaryCalculationUpdateMode {
  All = 'full',
  Linked = 'linked',
  None = 'none',
}

type PropsType = {
  form: UseFormReturn<SalaryProposalFormData>;
  currentEmploymentSalary: CurrentEmploymentSalary;
};

export const useSalaryRaiseFields = (props: PropsType) => {
  const {
    form: {
      control,
      setValue,
      getValues,
      formState: { dirtyFields },
      trigger,
    },
    currentEmploymentSalary,
  } = props;
  const { getEnums } = useAllEnums();
  const enableRecalculation = useMemo(() => {
    const { newSalary, raisePercentage, raiseAmount } = dirtyFields;

    return newSalary || raiseAmount || raisePercentage;
  }, [dirtyFields]);

  const frequency = useWatch({ control, name: 'salaryFrequency' });
  const baseSalary = useWatch({ control, name: 'baseSalary' });

  const { hasCurrentSalary, isCurrentSalaryFrequency, enableRaiseFeatures } =
    useMemo(() => {
      const hasCurrentSalary =
        currentEmploymentSalary.salaryFrequency.length > 0;
      const isCurrentSalaryFrequency =
        currentEmploymentSalary.salaryFrequency === frequency;

      return {
        hasCurrentSalary,
        isCurrentSalaryFrequency,
        enableRaiseFeatures: hasCurrentSalary && isCurrentSalaryFrequency,
      };
    }, [frequency, currentEmploymentSalary]);

  const recalculateSalaryChange = useCallback(
    (
      fieldName: string,
      newValue: number,
      newBaseSalary?: number,
    ): SalaryCalculation | undefined => {
      const base = !!newBaseSalary ? newBaseSalary : baseSalary;

      const percentage = (chunk: number, of: number): number =>
        (chunk / of) * 100;

      const currencyRounding = (value: number) => mathRoundToDecimals(value, 2);

      const resolveNewAmount = (fieldName: string, newValue: number) => {
        switch (fieldName) {
          case 'raiseAmount':
            return mathFloorToDecimals(newValue, 2);
          case 'raisePercentage':
            return currencyRounding(
              base * (mathFloorToDecimals(newValue, 3) / 100),
            );
          case 'newSalary':
            return mathFloorToDecimals(newValue, 2) - base;
          default:
            return undefined;
        }
      };
      const newAmount = resolveNewAmount(fieldName, newValue);

      if (typeof newAmount !== 'undefined') {
        const newPercentage =
          base !== 0
            ? mathRoundToDecimals(percentage(newAmount, base), 3)
            : 100;

        return {
          baseSalary: base,
          raiseAmount: currencyRounding(newAmount),
          raisePercentage: newPercentage,
          newSalary: currencyRounding(base + newAmount),
        };
      }
      return undefined;
    },
    [baseSalary],
  );

  const resolveCalculationUpdateMode = useCallback(
    (fieldName: string, fieldValue: string): SalaryCalculationUpdateMode => {
      const isPercentageField = fieldName === 'raisePercentage';
      const fieldDecimals = isPercentageField ? 3 : 2;

      const isNumber = fieldValue.match(regex_IsNumber);
      const matchesPattern = fieldValue.match(
        getRegex_IsNumberToDecimals(fieldDecimals),
      );

      // Determine if & how salary fields are to be updated
      if (isNumber && enableRaiseFeatures) {
        /**
         * If updating with Linked -method,
         * the onChange event will pass through,
         * otherwise all fields are updated with
         * a calculated value using setValue.
         *
         * There's a problem with values like
         * "1." & "1.0" being changed to "1"
         * on every key press, if original field
         * is always updated with setValue.
         */
        return matchesPattern
          ? SalaryCalculationUpdateMode.Linked
          : SalaryCalculationUpdateMode.All;
      }
      return SalaryCalculationUpdateMode.None;
    },
    [enableRaiseFeatures],
  );

  const updateRaiseField = useCallback(
    (props: {
      fieldName: FieldPath<SalaryProposalFormData>;
      calculatedValue: number;
      passEvent?: boolean;
      event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;
      onChange?: (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      ) => void;
    }) => {
      const { fieldName, calculatedValue, onChange, event, passEvent } = props;

      const canPassEvent = !!event && !!onChange;

      // Check if should update using original onChange or with setValue
      if (canPassEvent && !!passEvent) {
        onChange(event);
      } else {
        setValue(fieldName, calculatedValue);
      }
    },
    [setValue],
  );

  const updateRaiseFields = useCallback(
    (props: {
      calculation: SalaryCalculation;
      updateWithOnChange?: (
        fieldName: FieldPath<SalaryProposalFormData>,
      ) => boolean;
      event?: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>;
      onChange?: (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      ) => void;
    }) => {
      const { calculation, updateWithOnChange, event, onChange } = props;

      const updateFields: {
        name: FieldPath<SalaryProposalFormData>;
        value: number;
      }[] = [
        { name: 'raiseAmount', value: calculation.raiseAmount },
        { name: 'raisePercentage', value: calculation.raisePercentage },
        { name: 'newSalary', value: calculation.newSalary },
      ];

      updateFields.forEach((field) =>
        updateRaiseField({
          fieldName: field.name,
          calculatedValue: field.value,
          passEvent: !!updateWithOnChange
            ? updateWithOnChange(field.name)
            : false,
          event,
          onChange,
        }),
      );
      trigger(updateFields.map((f) => f.name));
    },
    [updateRaiseField, trigger],
  );

  const onRaiseFieldChange = useCallback(
    (
      event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      onChange: (
        e: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>,
      ) => void,
      fieldName: FieldPath<SalaryProposalFormData>,
    ) => {
      const eventValue = event.target.value;

      const updateMode = resolveCalculationUpdateMode(fieldName, eventValue);

      const shouldPassEvent = (
        updateFieldName: FieldPath<SalaryProposalFormData>,
      ): boolean => {
        // Check if updating the field that triggered recalculation
        const isEventOrigin = updateFieldName === fieldName;
        // Check if original event should pass to onChange callback
        const shouldPassOriginalEvent =
          updateMode === SalaryCalculationUpdateMode.Linked;

        // Check if should update using original onChange or with setValue
        return shouldPassOriginalEvent && isEventOrigin;
      };

      if (updateMode !== SalaryCalculationUpdateMode.None) {
        const newValue = parseFloat(eventValue);
        const calculation = recalculateSalaryChange(fieldName, newValue);

        if (!!calculation) {
          updateRaiseFields({
            calculation,
            updateWithOnChange: shouldPassEvent,
            event,
            onChange,
          });
        }
      } else {
        onChange(event);
      }
    },
    [recalculateSalaryChange, resolveCalculationUpdateMode, updateRaiseFields],
  );

  const resetCalculations = useCallback(
    (newBaseSalary: number) => {
      const newSalaryValue = getValues('newSalary');

      const calculation = recalculateSalaryChange(
        'newSalary',
        typeof newSalaryValue === 'number' ? newSalaryValue : 0,
        newBaseSalary,
      );

      if (!!calculation && enableRecalculation) {
        updateRaiseFields({
          calculation,
        });
      }
    },
    [
      recalculateSalaryChange,
      updateRaiseFields,
      getValues,
      enableRecalculation,
    ],
  );

  // Reset fields
  const frequencyChanged = useHasChanged(frequency);
  const currentEmploymentSalaryChanged = useHasChanged(currentEmploymentSalary);
  const shouldResetBaseSalary = useMemo(
    () => frequencyChanged || currentEmploymentSalaryChanged,
    [frequencyChanged, currentEmploymentSalaryChanged],
  );

  useEffect(() => {
    if (shouldResetBaseSalary) {
      const newBaseSalaryValue: number =
        currentEmploymentSalary.salaryFrequency === frequency &&
        !isNaN(currentEmploymentSalary.baseSalary)
          ? currentEmploymentSalary.baseSalary
          : 0;

      setValue('baseSalary', newBaseSalaryValue);

      if (enableRaiseFeatures) {
        resetCalculations(newBaseSalaryValue);
      }
    }
  }, [
    frequency,
    currentEmploymentSalary,
    shouldResetBaseSalary,
    enableRaiseFeatures,
    resetCalculations,
    setValue,
  ]);

  // Get enum name
  const getFrequencyEnumName = (enumCode: string): string | undefined => {
    return getEnums(EnumListKey.EVENT_FREQUENCY).find(
      (e) => e.code === enumCode,
    )?.name;
  };

  return {
    onRaiseFieldChange,
    frequency,
    getFrequencyEnumName,
    showRaiseFields: enableRaiseFeatures,
    hasCurrentSalary,
    isCurrentSalaryFrequency,
  };
};
